import { ApartmentOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Select, Spin, TreeSelect, message } from 'antd';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAllCategories, getProductDetails, importCmsProduct, searchProducts } from '../../api/product';
import Languages from '../../library/dynamics/languages';
import { getProductThumbnailUrl, productUrl } from '../../library/helpers/product_url';
import { formatPrice } from '../../library/utilities/intl';
import { ErpProductTag } from '../design';
import ProductCreateModal from './ProductCreateModal.jsx';

// English (UK entity) is priced in GBP, every other storefront language is EUR.
const getDisplayCurrency = (language) => (language === 'en' ? 'GBP' : 'EUR')

function ProductSelect({ language, value = null, selectedProductUrl, onChange, currentSelection = {}, disabled = false, ...otherProps }) {
    const debounceTimeout = 800
    const [fetching, setFetching] = useState(false)
    const [product, setProduct] = useState({})
    const [categories,
        setCategories
    ] = useState([])
    const [selectedCategory, setSelectedCategory] = useState()
    const [options, setOptions] = useState([{ value: currentSelection?.id, label: currentSelection?.name }]);
    const fetchRef = useRef(0);
    const searchQueryRef = useRef('')
    const [searchText, setSearchText] = useState('')
    const [createOpen, setCreateOpen] = useState(false)
    const pageRef = useRef(1)
    const hasMoreRef = useRef(false)
    const fetchingMoreRef = useRef(false)
    const optionsRef = useRef([])
    const categoryTreeRef = useRef(null)
    const isMountedRef = useRef(true)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (currentSelection?.id) {
            setProduct(currentSelection)
            setSelectedCategory(currentSelection.category_id)
            // getProductDetails(currentSelection.id, language)
            //     .then((data) => {
            //         // setSelectedCategory(data?.category_id)
            //         // onChange(data)
            //         // console.log(data);
            //         setProduct(data);
            //     })
        }

        debounceFetcher(null, currentSelection?.category_id)
        getAllCategories(language)
            .then(data => {
                if (!isMountedRef.current) {
                    return
                }
                setCategories(data)
                // searchProducts(data)
            })
    }, [])

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value = null, categoryId = null) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            optionsRef.current = [];
            pageRef.current = 1;
            hasMoreRef.current = false;
            if (!categoryId && (!value || value.length < 3)) {
                return []
            }
            setFetching(true);
            searchProducts(value, categoryId, language, 1).then(({ options, hasMore }) => {
                if (!isMountedRef.current || fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }
                optionsRef.current = options;
                hasMoreRef.current = hasMore;
                setOptions(options);
                setFetching(false);
            }).catch(() => {
                if (!isMountedRef.current || fetchId !== fetchRef.current) {
                    return;
                }
                setFetching(false);
            });
        };
        return debounce(loadOptions, debounceTimeout);
        // `categoryId` is always passed explicitly by callers, so `selectedCategory`
        // is not a real dependency — including it recreated the debounce timer
        // (and dropped any pending search) on every category change.
    }, [language, searchProducts, debounceTimeout]);

    // Appends the next page while scrolling the product dropdown. New ids are
    // merged in, so a server without paging (repeating page 1) just stops us
    // instead of duplicating rows.
    const fetchMoreOptions = () => {
        if (fetchingMoreRef.current || !hasMoreRef.current) {
            return
        }
        fetchingMoreRef.current = true;
        const fetchId = fetchRef.current;
        const nextPage = pageRef.current + 1;
        searchProducts(searchQueryRef.current, selectedCategory, language, nextPage)
            .then(({ options, hasMore }) => {
                fetchingMoreRef.current = false;
                if (!isMountedRef.current || fetchId !== fetchRef.current) {
                    return;
                }
                const seen = new Set(optionsRef.current.map((o) => o.value));
                const fresh = options.filter((o) => !seen.has(o.value));
                if (fresh.length === 0) {
                    hasMoreRef.current = false;
                    return;
                }
                pageRef.current = nextPage;
                hasMoreRef.current = hasMore;
                optionsRef.current = [...optionsRef.current, ...fresh];
                setOptions(optionsRef.current);
            })
            .catch(() => {
                fetchingMoreRef.current = false;
            });
    };

    const handleProductPopupScroll = (e) => {
        const target = e.target;
        if (target.scrollTop + target.clientHeight < target.scrollHeight - 60) {
            return
        }
        fetchMoreOptions();
    };

    const handleSelect = async (id) => {
        if (!id) {
            setProduct({})
            onChange(null)
            return
        }
        // Picking a product is the one moment it is synced from the CMS: a hit
        // not in the ERP yet is created, an existing one refreshed. Afterwards
        // everything reads it from the ERP tables.
        let sync = true
        if (typeof id === 'string' && id.startsWith('cms:')) {
            const option = optionsRef.current.find((o) => o.value === id)
            const hide = message.loading('Loading product from CMS…', 0)
            try {
                const { id: importedId } = await importCmsProduct(id.slice(4), language)
                optionsRef.current = optionsRef.current.map((o) => (o.value === id ? { ...o, value: importedId } : o))
                setOptions(optionsRef.current)
                id = importedId
                sync = false // just created from the CMS
            }
            catch (e) {
                message.error(e.message)
                return
            }
            finally {
                hide()
            }
            if (option && !optionsRef.current.some((o) => o.value === id)) {
                optionsRef.current = [{ ...option, value: id }, ...optionsRef.current]
                setOptions(optionsRef.current)
            }
        }
        getProductDetails(id, language, null, { sync })
            .then((data) => {
                setSelectedCategory(data?.category_id)
                onChange(data)
                setProduct(data);

            })
    }

    // A product just created in the modal is selected like a search hit,
    // which on Create Item also creates the item.
    // The Select clears its own input on close without calling onSearch,
    // so the typed text is dropped here too (kept while the modal uses it).
    const closeCreateModal = () => {
        setCreateOpen(false)
        setSearchText('')
    }

    const handleProductCreated = ({ id, name }) => {
        closeCreateModal()
        if (!optionsRef.current.some((o) => o.value === id)) {
            optionsRef.current = [{ value: id, label: name, is_erp_product: true }, ...optionsRef.current]
            setOptions(optionsRef.current)
        }
        handleSelect(id)
    }

    // Main (top-level) categories are navigation only — product search runs
    // on subcategories, so mains are shown but not selectable.
    const categoryTreeData = useMemo(
        () => (categories || []).map((node) => ({ ...node, selectable: false })),
        [categories],
    );

    return (
        <>
            <Input.Group compact>
                <TreeSelect
                    ref={categoryTreeRef}
                    // rc-tree ships an old nested rc-virtual-list (3.14.x) that cuts off the
                    // last row; the list is short, so plain native scrolling is safer
                    virtual={false}
                    // the dropdown keeps its last scroll position; without a
                    // selection start at the top (with one, antd scrolls to it)
                    onOpenChange={(open) => {
                        if (open && !selectedCategory) {
                            setTimeout(() => categoryTreeRef.current?.scrollTo(0))
                        }
                    }}
                    showSearch
                    value={selectedCategory}
                    style={{ width: '30%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeData={categoryTreeData}
                    fieldNames={{
                        label: 'name',
                        value: 'id',
                        children: 'children',
                    }}
                    onChange={(category_id) => {
                        setSelectedCategory(category_id)
                        debounceFetcher(searchQueryRef.current, category_id)
                        debounceFetcher.flush()
                    }}
                    placeholder="Subcategory"
                    /* onChange={onCategoryChange} */
                    suffixIcon={<ApartmentOutlined />}
                    allowClear
                    disabled={disabled}
                    treeNodeFilterProp='name'
                />
                <Select
                    filterOption={false}
                    onSearch={(v) => {
                        searchQueryRef.current = v
                        setSearchText(v)
                        debounceFetcher(v, selectedCategory)
                    }}
                    onPopupScroll={handleProductPopupScroll}
                    onDropdownVisibleChange={(open) => {
                        if (!open && !createOpen) {
                            setSearchText('')
                        }
                        // Clicking the product box with a category selected
                        // shows that category's products as defaults.
                        if (open && selectedCategory && options.length === 0 && !fetching) {
                            debounceFetcher(searchQueryRef.current, selectedCategory)
                            debounceFetcher.flush()
                        }
                    }}
                    // Never empty, so the dropdown (and its "add product"
                    // footer) still opens when nothing matches.
                    notFoundContent={fetching
                        ? <Spin size="small" />
                        : <span style={{ color: '#999' }}>{searchText.trim().length >= 3 || selectedCategory ? 'No matching product' : 'Type at least 3 letters to search'}</span>}
                    popupRender={(menu) => (
                        <>
                            {menu}
                            <Divider style={{ margin: '4px 0' }} />
                            <Button
                                type="text"
                                block
                                icon={<PlusOutlined />}
                                style={{ textAlign: 'left', color: 'var(--ant-color-primary, #1677ff)' }}
                                // keep focus in the search so the typed text survives
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setCreateOpen(true)}
                            >
                                {searchText.trim() ? `Add "${searchText.trim()}" as new product` : 'Add new product'}
                            </Button>
                        </>
                    )}
                    options={options}
                    optionRender={(option) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {option.data.photo && (
                                <img
                                    src={getProductThumbnailUrl(option.data.photo)}
                                    alt=""
                                    style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
                                />
                            )}
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.data.label}</span>
                            <ErpProductTag product={option.data} />
                            {option.data.starting_price != null && (
                                <span style={{ textAlign: 'right', flexShrink: 0, lineHeight: 1.2 }}>
                                    <div style={{ fontSize: 10, color: '#999' }}>Starting from</div>
                                    <div style={{ fontSize: 12, color: '#666' }}>
                                        {formatPrice(option.data.starting_price, getDisplayCurrency(language))}
                                    </div>
                                </span>
                            )}
                        </div>
                    )}
                    onChange={handleSelect}
                    value={value ?? currentSelection?.id}
                    placeholder="Select a Product"
                    suffixIcon={<SearchOutlined />}
                    style={{ width: '70%' }}
                    showSearch
                    allowClear
                    {...otherProps}
                    disabled={disabled}
                />
            </Input.Group>

            <ProductCreateModal
                open={createOpen}
                language={language}
                initialName={searchText}
                initialCategoryId={selectedCategory}
                categoryTreeData={categoryTreeData}
                onCancel={closeCreateModal}
                onCreated={handleProductCreated}
            />

            <div>
                {product.is_erp_product && (
                    <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>
                        <ErpProductTag product={product} style={{ marginInlineStart: 0, marginInlineEnd: 4 }} />
                        Created in ERP — not on the website
                    </div>
                )}
                {/* ERP-only products have no storefront page (url2 is null) */}
                {Object.keys(product).length > 0 && (selectedProductUrl ?? product.url2) && (
                    <>
                        <div style={{ marginBottom: 4 }}>Product URL:</div>
                        <Button
                            target="_blank"
                            href={productUrl(selectedProductUrl ?? product.url2, language)}
                            type="link"
                            style={{
                                whiteSpace: 'normal', 
                                wordBreak: 'break-all', 
                                textAlign: 'left',  
                                padding: 0     
                            }}
                        >
                            {productUrl(selectedProductUrl ?? product.url2, language)}
                        </Button>
                    </>
                )}
            </div>




            {/* <div style={{
                border:"2px solid red",
                display:"inline"
            }}>
                {Object.keys(product).length > 0 && (
                    <>
                        Product URL: <Button style={{width:"100%"}} target='blank' href={productUrl(selectedProductUrl ?? product.url2, language)} type='link'>{productUrl(selectedProductUrl ?? product.url2, language)}</Button>
                    </>
                )}
            </div> */}
        </>
    )
}

ProductSelect.propTypes = {
    language: PropTypes.oneOf(Languages.map(({ cms_language_code }) => cms_language_code)).isRequired,
    value: PropTypes.number,
    selectedProductUrl: PropTypes.string,
    onChange: PropTypes.func,
    currentSelection: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
    }),
    disabled: PropTypes.bool,
}

export default ProductSelect;